import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Share2, Copy, CheckCircle2, Play } from "lucide-react";
import { toast } from "sonner";

const VIDEO_URL = `${process.env.REACT_APP_BACKEND_URL}/ads/afrimarket-ad-vertical.mp4`;
const SHARE_TEXT = "Buy and sell across Africa & diaspora on AfriMarket — Ankara, jollof, braids, handyman, childminding. Trusted sellers, secure payments. https://afrimarket.org";

const ShareButton = ({ href, label, color, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-white font-semibold text-sm transition-transform hover:-translate-y-0.5"
    style={{ background: color }}
  >
    <Share2 className="w-4 h-4" />
    {label}
  </a>
);

const ShareAd = () => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(VIDEO_URL);
      setCopied(true);
      toast.success("Video link copied — paste into any post");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_TEXT);
      toast.success("Caption copied — paste into your post");
    } catch {
      toast.error("Could not copy");
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://afrimarket.org")}&quote=${encodeURIComponent(SHARE_TEXT)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}`;

  return (
    <div className="min-h-screen bg-[#F3EFE9] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <div className="label-overline text-[#C05A3C] mb-2">Marketing</div>
          <h1 className="font-display text-4xl sm:text-5xl">Your AfriMarket video ad</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            8-second vertical clip, ready for TikTok, Instagram Reels, WhatsApp Status, Facebook & X.
            Download once, post everywhere.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Video preview */}
          <Card className="overflow-hidden bg-black border-0 mx-auto" style={{ maxWidth: 360 }}>
            <video
              data-testid="ad-video-preview"
              src={VIDEO_URL}
              controls
              playsInline
              loop
              autoPlay
              muted
              className="w-full block"
              style={{ aspectRatio: "9/16", background: "#000" }}
            >
              Your browser does not support video.
            </video>
          </Card>

          {/* Actions */}
          <div className="space-y-6">
            <Card className="p-6 bg-white border-black/10">
              <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
                <Download className="w-5 h-5 text-[#C05A3C]" />
                Download
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Save the MP4 to your phone, then upload natively to each platform — gives the best reach.
              </p>
              <a
                href={VIDEO_URL}
                download="afrimarket-ad.mp4"
                data-testid="download-mp4-btn"
                className="inline-flex items-center gap-2 bg-[#1A2024] text-white px-5 py-3 rounded-lg font-semibold hover:bg-[#233348] transition-colors"
              >
                <Download className="w-4 h-4" />
                Download MP4 (vertical 9:16)
              </a>
            </Card>

            <Card className="p-6 bg-white border-black/10">
              <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[#C05A3C]" />
                One-tap share
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Quick share with caption pre-filled. (TikTok and Instagram don&apos;t allow direct video upload from
                links — use the Download button, then upload in the app.)
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                <ShareButton href={whatsappUrl} label="WhatsApp" color="#25D366" testId="share-whatsapp" />
                <ShareButton href={facebookUrl} label="Facebook" color="#1877F2" testId="share-facebook" />
                <ShareButton href={twitterUrl} label="X / Twitter" color="#000000" testId="share-twitter" />
                <button
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                  className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 bg-[#C05A3C] text-white font-semibold text-sm transition-transform hover:-translate-y-0.5"
                >
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Link copied" : "Copy video link"}
                </button>
              </div>

              <div className="pt-4 border-t border-black/5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Suggested caption</div>
                <p className="text-sm bg-[#F3EFE9] p-3 rounded-md leading-relaxed">{SHARE_TEXT}</p>
                <Button
                  onClick={copyCaption}
                  variant="outline"
                  size="sm"
                  data-testid="copy-caption-btn"
                  className="mt-3 border-[#1A2024] text-[#1A2024]"
                >
                  <Copy className="w-3 h-3 mr-2" />
                  Copy caption
                </Button>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-[#C05A3C] to-[#1A2024] text-white border-0">
              <div className="flex items-start gap-3">
                <Play className="w-6 h-6 shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-xl">Posting tips for maximum reach</h3>
                  <ul className="mt-3 text-sm space-y-2 list-disc list-inside opacity-90">
                    <li>Post natively (upload in-app) rather than sharing a link — algorithms favour native</li>
                    <li>Post 3-4×/week on each platform, not once and forget</li>
                    <li>TikTok prime times: 6-10pm. Instagram: 11am-2pm. WhatsApp Status: anytime</li>
                    <li>Pin the video on your Facebook page for new visitors</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareAd;
